import { useEffect, useRef, useState, useContext } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./sidebar/Sidebar";
import ImageContainer from "../imageContainer/ImageContainer";
import { SEARCH_PARAMS } from "../../utils/SearchParams";
import { useCustomSearchParams } from "../hooks/useCustomSearchParams";
import AxiosService from "../../utils/AxiosService";
import { AppContext } from "../../context/AppContext";
import { KEY_NAME } from "../../utils/KeyName";

import "./DashboardLayout.scss";

const NO_SEARCH = "NO_SEARCH"
const FILTER_BY_PAGE_SIZE = 200;

function defaultFilterBYOption(entity) {
  if (entity === KEY_NAME.FUNDER) {
    return [
      {
        label: `All publishers`,
        value: "ALL",
      },
      // {
      //   label: `CHORUS publishers members`,
      //   value: "CHORUS_ALL",
      // },
    ];
  } else {
    return [
      {
        label: `All funders`,
        value: "ALL",
      },
      // {
      //   label: `CHORUS funders participants`,
      //   value: "CHORUS_ALL",
      // },
    ];
  }
}

function formatNumber(num) {
  return Number.isInteger(num) ? num : Number(num.toFixed(2));
}

function DashboardLayout({ children }) {
  // const { filterByData, selectedFilterBy, changeSelectedFilterBy } =
  //   useContext(AppContext);
  const { get, set, setMultiple } = useCustomSearchParams();
  // const [entityName, setEntityName] = useState(get(SEARCH_PARAMS.ENTITY_NAME));
  const [shortName, setShortName] = useState(get(SEARCH_PARAMS.SHORT_NAME));

  const axiosService = new AxiosService();
  const {
    entityName,
    filterByData,
    changeFilterByData,
    changeMetaInfo,
    selectedFilterBy,
    filterBySearchParams,
    selectedFilterByLabel,
    changeFilterByHasMore,
    changeFilterByLoadingMore,
    changeFilterByLoading,
    changeFilterByError,
    registerFilterByLoadMore,
  } = useContext(AppContext);

  // "Filter by" dropdown pagination: the API is fetched FILTER_BY_PAGE_SIZE
  // rows at a time. filterByPageRef tracks the last page fetched,
  // filterByAbortRef cancels a stale in-flight page request, and
  // filterByFetchingRef is a synchronous lock (state is async, so it can't
  // reliably prevent a second "load more" firing before the first request
  // is registered as loading).
  const filterByPageRef = useRef(1);
  const filterByAbortRef = useRef(null);
  const filterByFetchingRef = useRef(false);

  useEffect(() => {
    if (!(shortName && entityName)) return;

    // The effective search term for this fetch: either the user's live
    // search text, or (when nothing has been typed yet) a fallback to
    // the URL's FILTER_BY_NAME - used to restore a scoped fetch after a
    // page reload. filterBySearchParams alone doesn't capture that
    // fallback, so every "are we search-scoped?" decision below must
    // key off this instead, not the raw filterBySearchParams state.
    const entityContentSearch = [NO_SEARCH, "All funders", "All publishers"].includes(
      filterBySearchParams,
    )
      ? ""
      : filterBySearchParams || get(SEARCH_PARAMS.FILTER_BY_NAME) || "";
    const isSearchScoped = Boolean(entityContentSearch);

    // Fetches one page of "Filter by" options for the current entity/search
    // scope. append=false (a fresh entity/search) replaces filterByData and
    // resets pagination; append=true (triggered by scrolling the dropdown
    // to its end) tacks the page onto the existing list.
    const fetchFilterByPage = (page, append) => {
      filterByAbortRef.current?.abort();
      const controller = new AbortController();
      filterByAbortRef.current = controller;
      filterByFetchingRef.current = true;

      if (append) {
        changeFilterByLoadingMore(true);
      } else {
        // Don't wipe filterByData to just the default option here: doing so
        // blanks the dropdown on every keystroke while the scoped fetch is in
        // flight (most visible on the very first search, when the local list
        // has no optimistic match yet). Keep the current options visible and
        // let the response below replace them.
        changeFilterByHasMore(false);
        changeFilterByError(null);

        // Show the loading indicator for every fetch (initial load and every
        // subsequent search/navigation refetch), not just the first one —
        // the existing options stay visible underneath it since filterByData
        // is only ever seeded, never cleared, here.
        changeFilterByLoading(true);
        if (!filterByData) {
          changeFilterByData(defaultFilterBYOption(entityName));
        }
      }

      const finishFetch = () => {
        filterByFetchingRef.current = false;
        // If this request was aborted (Strict Mode double-invocation, or
        // a newer search replacing it), don't turn off loading — the newer
        // request owns the loading state now.
        if (controller.signal.aborted) return;
        if (append) {
          changeFilterByLoadingMore(false);
        } else {
          changeFilterByLoading(false);
        }
      };

      switch (entityName) {
        case KEY_NAME.FUNDER:
          axiosService
            .getPublisherReport(
              {
                entityContentSearch,
                page,
                size: FILTER_BY_PAGE_SIZE,
                sortKey: isSearchScoped ? "" : "publisher",
                sortOrder: isSearchScoped ? "" : "asc",
              },
              shortName,
              controller.signal,
            )
            .then((res) => {
              const responseData = res.data || [];

              const apiOptions =
                responseData?.data
                  ?.filter((item) => item.publisher)
                  .map((item) => ({
                    label: isSearchScoped
                      ? `\u00A0${item.publisher}`
                      : `\u00A0\u00A0\u00A0${item.publisher}`,
                    value: `CHORUS_MEMBER:${item.memberId}`,
                    shortName: item.shortName,
                  })) || [];

              const options = [
                ...(isSearchScoped || append
                  ? []
                  : [
                      {
                        label: "Select a Publisher member",
                        value: "",
                        disabled: true,
                      },
                    ]),
                ...apiOptions,
              ];

              changeFilterByData((prev) =>
                append && prev
                  ? [...prev, ...options]
                  : [...defaultFilterBYOption(entityName), ...options],
              );

              const total = responseData?.total ?? 0;
              changeFilterByHasMore(
                page * FILTER_BY_PAGE_SIZE < total && apiOptions.length > 0,
              );
            })
            .catch((err) => {
              if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return; // ignore aborts
              console.error("Failed to fetch publishers", err);
              // On initial load failure, fall back to hardcoded defaults so the
              // dropdown is still functional with at least the "All" option.
              if (!append) {
                changeFilterByData(
                  (prev) => prev ?? defaultFilterBYOption(entityName),
                );
              }
              changeFilterByError(
                err?.message || "Failed to load filter options",
              );
            })
            .finally(finishFetch);

          break;
        case KEY_NAME.PUBLISHER:
        case KEY_NAME.INSTITUTION:
          axiosService
            .getFundersReport(
              {
                entityContentSearch,
                page,
                size: FILTER_BY_PAGE_SIZE,
                sortKey: isSearchScoped ? "" : "funder",
                sortOrder: isSearchScoped ? "" : "asc",
                publisherFilter: "ALL"
              },
              shortName, controller.signal
            )
            .then((res) => {
              const responseData = res.data || [];

              const apiOptions =
                responseData?.data
                  ?.filter((item) => item.funder)
                  .map((item) => ({
                    label: isSearchScoped
                      ? `\u00A0${item.funder}`
                      : `\u00A0\u00A0\u00A0${item.funder}`,
                    value: `CHORUS_MEMBER:${item.agencyId}`,
                    shortName: item.shortName,
                  })) || [];

              const options = [
                ...(isSearchScoped || append
                  ? []
                  : [
                      {
                        label: "Select a Funder",
                        value: "",
                        disabled: true,
                      },
                    ]),
                ...apiOptions,
              ];

              changeFilterByData((prev) =>
                append && prev
                  ? [...prev, ...options]
                  : [...defaultFilterBYOption(entityName), ...options],
              );

              const total = responseData?.total ?? 0;
              changeFilterByHasMore(
                page * FILTER_BY_PAGE_SIZE < total && apiOptions.length > 0,
              );
            })
            .catch((err) => {
              if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return; // ignore aborts
              console.error("Error", err);
              // On initial load failure, fall back to hardcoded defaults so the
              // dropdown is still functional with at least the "All" option.
              if (!append) {
                changeFilterByData(
                  (prev) => prev ?? defaultFilterBYOption(entityName),
                );
              }
              changeFilterByError(
                err?.message || "Failed to load filter options",
              );
            })
            .finally(finishFetch);

          break;

        default:
          filterByFetchingRef.current = false;
          break;
      }
    };

    filterByPageRef.current = 1;
    fetchFilterByPage(1, false);

    registerFilterByLoadMore(() => {
      if (filterByFetchingRef.current) return;
      const nextPage = filterByPageRef.current + 1;
      filterByPageRef.current = nextPage;
      fetchFilterByPage(nextPage, true);
    });

    return () => filterByAbortRef.current?.abort(); // cancels stale request when effect re-runs or unmounts
  }, [shortName, entityName, filterBySearchParams]); //filterBySearchParams "Future code"

  useEffect(() => {
    if (selectedFilterBy || (shortName && entityName)) {
      switch (entityName) {
        case KEY_NAME.FUNDER:
          axiosService
            .getFundersMetaInfo(selectedFilterBy?.split(":")[1], shortName)
            .then((res) => {
              const data = res.data;
              changeMetaInfo(() => ({
                ...data,
                total: data.totalPublications,
                percentIncrease: formatNumber(data.percentIncrease),
              }));
            })
            .catch((err) => {
              console.error("Failed to fetch Funder Meta info", err);
            })
            .finally(() => {});

          break;
        case KEY_NAME.PUBLISHER:
          // case KEY_NAME.INSTITUTION:
          axiosService
            .getPublisherMetaInfo(selectedFilterBy?.split(":")[1], shortName)
            .then((res) => {
              const data = res.data;
              changeMetaInfo(() => ({
                ...data,
                total: data.totalFunders,

                percentIncrease: formatNumber(data.percentIncrease),
              }));
            })
            .catch((err) => {
              console.error("Error", err);
            })
            .finally(() => {});

          break;
        case KEY_NAME.INSTITUTION:
          axiosService
            .getPublisherMetaInfo(selectedFilterBy?.split(":")[1], shortName)
            .then((res) => {
              const data = res.data;
              changeMetaInfo((prev) => ({
                ...prev,
                ...data,
                total: data.totalFunders,

                percentIncrease: formatNumber(data.percentIncrease),
              }));
            })
            .catch((err) => {
              console.error("Error", err);
            })
            .finally(() => {});

          axiosService
            .getInstitutionRorId(shortName)
            .then((res) => {
              const rorLink = res.data?.id;
              const rorId = rorLink?.split("/").pop();
              changeMetaInfo((prev) => ({
                ...prev,
                rorLink,
                rorId,
              }));
            })
            .catch((err) => {
              console.error("Error fetching ROR ID", err);
            })
            .finally(() => {});
          break;

        default:
          break;
      }
    }
  }, [selectedFilterBy, shortName, entityName]);

  return (
    <div className="d-flex min-vh-100">
      <aside className="sidebar border-end ">
        <Sidebar />
      </aside>
      <div className="main-layout d-flex flex-column">
        <main className="p-4">
          <Outlet />
        </main>
        <div className="my-5 mt-auto px-4">
          <ImageContainer />
        </div>
      </div>
    </div>
  );
}

export default DashboardLayout;
